'use client';

// PackComparisonBlock — tableau comparatif de packs avec :
//   • Toggle "Paiement mensuel / annuel" en haut (switch les prix affichés)
//   • Header sticky : noms+prix+bouton restent visibles au scroll
//   • Bouton "Choisir ce pack" sous chaque colonne
//   • Sections + lignes avec valeurs par colonne
//
// Structure config :
//   {
//     title: 'Comparez nos packs',
//     subtitle: '',
//     show_billing_toggle: true,            // toggle Mensuel/Annuel
//     default_billing: 'monthly',           // 'monthly' | 'yearly'
//     pack_columns: [
//       {
//         label: 'Starter',
//         price_monthly: '0€',
//         price_yearly: '0€',
//         subtitle_monthly: '/ mois, sans engagement',
//         subtitle_yearly: '/ mois, payé annuellement',
//         highlight: false,
//         badge: '',
//         cta_text: 'Choisir ce pack',
//         cta_url: 'https://app.ligobox.com/signup?pack=starter',
//       },
//     ],
//     sections: [...] // comme avant
//   }

import { useState } from 'react';
import { Check, X } from 'lucide-react';

export default function PackComparisonBlock({ config }) {
  const cols = Array.isArray(config?.pack_columns) ? config.pack_columns : [];
  const sections = Array.isArray(config?.sections) ? config.sections : [];
  const showToggle = config?.show_billing_toggle !== false;
  const defaultBilling = config?.default_billing === 'yearly' ? 'yearly' : 'monthly';
  const [billing, setBilling] = useState(defaultBilling);

  if (cols.length === 0 || sections.length === 0) return null;

  const nCols = cols.length;
  const gridCols = `minmax(220px, 1.6fr) ${cols.map(() => 'minmax(160px, 1fr)').join(' ')}`;

  return (
    <section style={{ padding: '36px 0', background: 'var(--color-bg, #fff)' }}>
      <div className="container-page">
        {(config.title || config.subtitle) && (
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            {config.title && (
              <h2 style={{
                fontSize: 'clamp(28px, 4vw, 38px)',
                fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em',
                margin: '0 0 12px', color: 'var(--color-text, #0f172a)',
              }}>{config.title}</h2>
            )}
            {config.subtitle && (
              <p style={{
                fontSize: 16, color: 'var(--color-text-muted, #64748b)',
                margin: 0, maxWidth: 700, marginLeft: 'auto', marginRight: 'auto',
              }}>{config.subtitle}</p>
            )}
          </div>
        )}

        {/* Toggle Mensuel / Annuel */}
        {showToggle && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
            <div style={{
              display: 'inline-flex',
              padding: 4,
              background: 'var(--color-bg-alt, #f1f5f9)',
              borderRadius: 999,
              border: '1px solid var(--color-border, #e2e8f0)',
            }}>
              <ToggleBtn active={billing === 'monthly'} onClick={() => setBilling('monthly')}>
                Paiement mensuel
              </ToggleBtn>
              <ToggleBtn active={billing === 'yearly'} onClick={() => setBilling('yearly')}>
                Paiement annuel
              </ToggleBtn>
            </div>
          </div>
        )}

        {/*
          IMPORTANT — comportement sticky du header :
          Pas d'overflow sur le wrapper exterieur, sinon position:sticky
          stickerait au wrapper et pas a la page. Le header sticky doit etre
          relatif au document → on le met directement dans le flux principal,
          PUIS on met le tableau en dessous. Pas de scroll horizontal special
          mais le tableau peut etre tres large : on accepte qu'il deborde
          horizontalement (l'utilisateur scrollera horizontalement avec la
          page si besoin sur petit ecran).
        */}
        {/* V1.25.959 — Wrapper scroll horizontal MOBILE UNIQUEMENT.
            Sur desktop : overflow visible → position:sticky du header marche.
            Sur mobile (<768px) : overflow-x: auto + min-width 760px appliques
            via CSS @media (cf globals.css .pack-comparison-scroll). */}
        <div className="pack-comparison-scroll">
        <div style={{
          border: '1px solid var(--color-border, #e2e8f0)',
          borderRadius: 12,
          background: '#fff',
          overflow: 'visible', // CRITIQUE — sinon sticky casse
        }}>
          {/* HEADER STICKY : reste accroche en haut du viewport au scroll */}
          <div style={{
            position: 'sticky',
            top: 72, // sous la navbar Ligobox (height ~72px)
            zIndex: 10,
            background: '#fff',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            boxShadow: '0 6px 12px -8px rgba(0,0,0,0.12)',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              borderBottom: '2px solid var(--color-border, #e2e8f0)',
            }}>
              <div></div>
              {cols.map((col, i) => (
                <PackHeader key={i} col={col} billing={billing} />
              ))}
            </div>
          </div>

          {/* SECTIONS — chaque titre de section est ELLE-MEME sticky, juste
              en-dessous du pack header sticky. Permet a l'utilisateur de
              toujours savoir dans quelle section il se trouve, et evite que
              la 1ere ligne soit cachee. */}
          {sections.map((section, si) => (
            <div key={si}>
              <div style={{
                position: 'sticky',
                // Le pack header fait ~190px (sticky a top:72px), donc la section
                // title se cale juste en dessous a 72+190 = 262px
                top: 262,
                zIndex: 5,
                padding: '14px 20px',
                background: 'var(--color-bg-alt, #f8fafc)',
                borderTop: si > 0 ? '1px solid var(--color-border, #e2e8f0)' : 'none',
                borderBottom: '1px solid var(--color-border, #e2e8f0)',
                fontSize: 14, fontWeight: 700,
                color: 'var(--color-text, #0f172a)',
                letterSpacing: '0.01em',
              }}>
                {section.title || ''}
              </div>
              {(section.rows || []).map((row, ri) => (
                <div key={ri} style={{
                  display: 'grid',
                  gridTemplateColumns: gridCols,
                  borderBottom: '1px solid var(--color-border, #f1f5f9)',
                  alignItems: 'center',
                }}>
                  <div style={{
                    padding: '14px 20px',
                    fontSize: 14,
                    color: 'var(--color-text, #0f172a)',
                  }}>
                    {row.label || ''}
                  </div>
                  {Array.from({ length: nCols }).map((_, ci) => (
                    <Cell key={ci} value={(row.values || [])[ci]} highlight={cols[ci]?.highlight} />
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
        </div>{/* /pack-comparison-scroll V1.25.957 */}

        {/* ── PACKS_MOBILE_V1 (12/08/2026, demande Gaetan) ──
            Sur mobile, plus de tableau large à faire défiler : les packs
            s'empilent L'UN SOUS L'AUTRE, chacun en carte complète (en-tête
            prix/CTA + toutes ses lignes par section). La bascule
            desktop/mobile est en CSS pur (globals.css) : le tableau garde
            son header sticky sur desktop, strictement inchangé. */}
        <div className="pack-comparison-mobile">
          {cols.map((col, ci) => (
            <div key={ci} style={{
              border: col.highlight
                ? '2px solid var(--color-primary, #e27228)'
                : '1px solid var(--color-border, #e2e8f0)',
              borderRadius: 12, background: '#fff', overflow: 'hidden',
              marginBottom: 16,
            }}>
              <PackHeader col={col} billing={billing} />
              {sections.map((section, si) => (
                <div key={si}>
                  <div style={{
                    padding: '10px 14px',
                    background: 'var(--color-bg-alt, #f8fafc)',
                    borderTop: '1px solid var(--color-border, #e2e8f0)',
                    borderBottom: '1px solid var(--color-border, #e2e8f0)',
                    fontSize: 13, fontWeight: 700,
                    color: 'var(--color-text, #0f172a)',
                  }}>
                    {section.title || ''}
                  </div>
                  {(section.rows || []).map((row, ri) => (
                    <div key={ri} style={{
                      display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', gap: 8,
                      borderBottom: '1px solid var(--color-border, #f1f5f9)',
                      paddingLeft: 14,
                    }}>
                      <div style={{ fontSize: 13, padding: '10px 0', color: 'var(--color-text, #0f172a)' }}>
                        {row.label || ''}
                      </div>
                      <Cell value={(row.values || [])[ci]} highlight={col.highlight} />
                    </div>
                  ))}
                </div>
              ))}
              {/* PACKS_MOBILE_V2 (12/08/2026, demande Gaetan) — bouton de
                  conversion COLLÉ à la dernière ligne de la carte : après
                  avoir lu tout le détail du pack, le visiteur n'a pas à
                  remonter jusqu'à l'en-tête pour choisir. */}
              <div style={{ padding: '12px 14px' }}>
                <a
                  href={packCtaUrl(col)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '11px 12px', fontSize: 14, fontWeight: 700,
                    borderRadius: 8, textDecoration: 'none',
                    background: col.highlight ? 'var(--color-primary, #e27228)' : '#fff',
                    color: col.highlight ? 'var(--color-primary-text, #fff)' : 'var(--color-primary, #e27228)',
                    border: col.highlight ? 'none' : '1px solid var(--color-primary, #e27228)',
                  }}
                >
                  {`Choisir ${col.label || 'ce pack'}`}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ToggleBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 18px',
        border: 'none',
        background: active ? 'var(--color-primary, #e27228)' : 'transparent',
        color: active ? 'var(--color-primary-text, #fff)' : 'var(--color-text, #0f172a)',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {children}
    </button>
  );
}

// PACKS_MOBILE_V2 — URL du CTA d'un pack, partagée entre l'en-tête du
// tableau et le bouton bas de carte mobile (même logique V1.25.952 :
// cta_url custom respecté, sinon /signup avec le slug en analytics).
function packCtaUrl(col) {
  return col.cta_url && col.cta_url !== ''
    ? col.cta_url
    : (col.pack_slug
        ? `https://app.ligobox.com/signup?pack=${col.pack_slug}`
        : 'https://app.ligobox.com/signup');
}

// V1.25.958 — Parse un prix textuel comme "39€" ou "29,50 €" → nombre.
// Renvoie null si pas de chiffre detectable (ex: "sur devis").
function parsePrice(s) {
  if (s == null) return null;
  const m = String(s).replace(',', '.').match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

function PackHeader({ col, billing }) {
  const isYearly = billing === 'yearly';
  const price = isYearly
    ? (col.price_yearly || col.price_monthly || col.price || '')
    : (col.price_monthly || col.price || '');
  const subtitle = isYearly
    ? (col.subtitle_yearly || col.subtitle_monthly || col.subtitle || '')
    : (col.subtitle_monthly || col.subtitle || '');
  const ctaText = col.cta_text || 'Choisir ce pack';
  // V1.25.958 — Calcul economie annuelle. Le prix annuel est exprime en
  // "X€/mois (paye annuellement)" donc economie = (monthly - yearly) * 12.
  const monthlyN = parsePrice(col.price_monthly);
  const yearlyN = parsePrice(col.price_yearly);
  const yearlySaving = (isYearly && monthlyN && yearlyN && yearlyN < monthlyN)
    ? Math.round((monthlyN - yearlyN) * 12)
    : 0;
  // V1.25.952 — Le bouton de la vitrine pointe TOUJOURS vers /signup pour creer
  // un compte. Apres inscription, le pack par defaut Starter (0€) est attribue
  // automatiquement, puis l'utilisateur est redirige vers /admin/packs ou il
  // peut choisir/payer le pack qui l'interesse. Le slug est passe en query pour
  // analytics. Si admin a defini un cta_url custom (ex: /#contact pour Business
  // sur devis), on respecte ce choix.
  const ctaUrl = packCtaUrl(col);

  // V1.25.958 — Layout en 6 zones (badge / nom / prix / subtitle / saving badge / CTA).
  // La 5e zone (badge "Vous economisez") a une hauteur fixe (22px) pour aligner
  // les colonnes meme si pas d'economie.
  return (
    <div style={{
      padding: '12px 14px 14px',
      textAlign: 'center',
      background: col.highlight ? 'rgba(226,114,40,0.06)' : '#fff',
      display: 'grid',
      gridTemplateRows: '20px auto 50px 22px 22px 38px',
      rowGap: 4,
      alignItems: 'center',
      justifyItems: 'center',
    }}>
      {/* Zone 1 : badge (hauteur fixe meme si vide pour alignement) */}
      <div style={{ height: 20, display: 'flex', alignItems: 'center' }}>
        {col.badge && (
          <span style={{
            fontSize: 10, fontWeight: 700,
            padding: '3px 8px', borderRadius: 999,
            background: 'var(--color-primary, #e27228)',
            color: 'var(--color-primary-text, #fff)',
            letterSpacing: '0.05em',
          }}>{col.badge}</span>
        )}
      </div>
      {/* Zone 2 : nom du pack */}
      <div style={{
        fontSize: 14, fontWeight: 700,
        color: 'var(--color-text, #0f172a)',
      }}>{col.label || ''}</div>
      {/* Zone 3 : prix (hauteur fixe pour aligner les "0€", "39€", "sur demande") */}
      <div style={{
        fontSize: price && price.length > 8 ? 18 : 26,
        fontWeight: 800, lineHeight: 1.1,
        color: col.highlight ? 'var(--color-primary, #e27228)' : 'var(--color-text, #0f172a)',
        whiteSpace: 'nowrap',
      }}>{price || ' '}</div>
      {/* Zone 4 : subtitle (hauteur fixe meme si vide) */}
      <div style={{
        fontSize: 11,
        color: 'var(--color-text-muted, #64748b)',
        minHeight: 14,
      }}>{subtitle || ' '}</div>
      {/* Zone 5 (V1.25.958) : badge "Vous économisez XX €" — hauteur fixe pour
          aligner les colonnes meme quand pas d'economie. */}
      <div style={{ height: 22, display: 'flex', alignItems: 'center' }}>
        {yearlySaving > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 700,
            padding: '3px 8px', borderRadius: 999,
            background: '#d1fae5', color: '#065f46',
            border: '1px solid #a7f3d0',
            whiteSpace: 'nowrap',
          }}>
            Vous économisez {yearlySaving} €
          </span>
        )}
      </div>
      {/* Zone 6 : bouton CTA (toujours rendu si url) */}
      <div>
        {ctaUrl ? (
          <a
            href={ctaUrl}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 8,
              textDecoration: 'none',
              background: col.highlight ? 'var(--color-primary, #e27228)' : '#fff',
              color: col.highlight ? 'var(--color-primary-text, #fff)' : 'var(--color-primary, #e27228)',
              border: col.highlight ? 'none' : '1px solid var(--color-primary, #e27228)',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {ctaText}
          </a>
        ) : null}
      </div>
    </div>
  );
}

function Cell({ value, highlight }) {
  // Trim systematique : si la valeur est ' ' (marqueur "mode texte vide"
  // pose par l'admin quand on clique T sans rien taper), on la traite
  // comme une cellule vide → rend un X gris (cross). Evite l'effet "cellule
  // blanche dans le tableau" qu'on voyait quand l'admin n'avait pas fini
  // de taper son texte.
  const v = (value || '').toString().trim();
  const wrap = (children) => (
    <div style={{
      padding: '14px 16px',
      textAlign: 'center',
      fontSize: 13,
      background: highlight ? 'rgba(226,114,40,0.04)' : 'transparent',
      color: 'var(--color-text, #0f172a)',
    }}>{children}</div>
  );

  if (!v || v === '-' || v === 'cross' || v === 'non' || v === 'no' || v === '✗') {
    // Empty / whitespace-only / 'cross' → rend toujours un X cerclé gris
    return wrap(<span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22, borderRadius: 999,
      background: '#e2e8f0',
      color: '#94a3b8',
    }}><X size={14} strokeWidth={3} /></span>);
  }
  if (v === 'check' || v === 'oui' || v === 'yes' || v === '✓') {
    return wrap(<span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 22, height: 22, borderRadius: 999,
      background: 'var(--color-text, #0f172a)',
      color: '#fff',
    }}><Check size={14} strokeWidth={3} /></span>);
  }
  // Texte libre (Option, Basique, Max. 30/an, 0,25€/SMS, etc.)
  return wrap(<span style={{ fontWeight: 500 }}>{v}</span>);
}
